<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container class="px-10 pt-8">
        <p class="subtitle-1 font-weight-bold mb-2">Create combined payment</p>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Tenant:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">
              {{ tenantName }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Unit:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">
              {{ unitName }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Balance:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(-tenantBalance) }}
            </p>
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>
        <v-form v-model="isFormValid">
          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="6">
              <v-currency-field
                v-model="amount"
                type="number"
                label="Total amount"
                prepend-inner-icon="mdi-cash"
                disabled
                outlined
                dense
                hide-details
              >
              </v-currency-field>
            </v-col>
            <v-col class="py-1" cols="12" sm="12" md="6">
              <v-currency-field
                v-model="amountPaid"
                type="number"
                label="Amount Paid"
                prepend-inner-icon="mdi-cash"
                outlined
                dense
                hide-details
              >
              </v-currency-field>
            </v-col>
          </v-row>

          <v-row v-if="tenantBalance > 0">
            <v-col class="pt-0 pb-2" cols="12" sm="12" md="12">
              <v-checkbox
                v-model="useRemainingBalance"
                label="Use Remaining Credits"
                class="my-0"
                hide-details
                :disabled="!tenantBalance"
                @change="useRemainingBalanceEvent"
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-expansion-panels
            v-if="showSecurityDeposit"
            class="pb-2 my-2"
            accordion
            flat
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="py-2 body-2"
                >Security deposit found. Click here for more
                details</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <v-divider class="mb-0"></v-divider>
                <!-- Available security deposits -->
                <v-list-item
                  v-for="securityDeposit in securityDeposits"
                  :key="securityDeposit.securityDepositId"
                  three-line
                >
                  <v-list-item-avatar size="30">
                    <v-icon class="grey lighten-1" small dark>
                      mdi-cash
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">
                      Total amount:
                      {{
                        formatCurrency(securityDeposit.amount)
                      }}</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-if="securityDeposit.description"
                      class="caption"
                    >
                      {{ securityDeposit.description }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else class="caption grey--text">
                      No description provided
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="caption">
                      Usable amount:
                      {{ formatCurrency(securityDeposit.usable) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mb-1"></v-divider>

                <div class="mb-4 d-flex flex-row justify-space-between">
                  <p class="caption">Total usable security deposit:</p>
                  <p class="font-weight-bold">
                    {{ formatCurrency(maxSecurityDepositAmount) }}
                  </p>
                </div>

                <v-row>
                  <v-col class="py-0 pb-2" cols="12" sm="12" md="12">
                    <v-switch
                      v-model="useSecurityDeposit"
                      class="my-0 mb-2"
                      label="Use security deposit?"
                      hide-details
                      inset
                      dense
                      @change="useRemainingBalanceEvent"
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0" cols="12" sm="12" md="12">
                    <v-currency-field
                      v-model="securityDepositAmountUsed"
                      label="Deducted amount"
                      :disabled="!useSecurityDeposit"
                      :rules="
                        useSecurityDeposit
                          ? securityDepositAmountRules
                          : undefined
                      "
                      outlined
                      dense
                    >
                    </v-currency-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-expansion-panels class="pb-2 my-2" accordion flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="py-2 body-2"
                >Payment Requests
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-divider class="mb-0"></v-divider>

                <v-list-item
                  v-for="paymentRequest in paymentRequests"
                  :key="`payment_request_${paymentRequest.paymentId}`"
                  three-line
                >
                  <v-list-item-avatar color="grey">
                    <p class="caption white--text">
                      {{ paymentRequest.refNo }}
                    </p>
                    <!-- <v-icon class="grey lighten-1" dark> mdi-cash </v-icon> -->
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">
                      Total amount:
                      {{
                        formatCurrency(paymentRequest.total)
                      }}</v-list-item-title
                    >
                    <v-list-item-subtitle class="caption">
                      Category:
                      {{ paymentRequest.category }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle
                      v-if="paymentRequest.amountPaid"
                      class="caption"
                    >
                      Amount paid:
                      {{ formatCurrency(paymentRequest.amountPaid) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else class="caption grey--text">
                      No previous payment
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mb-1"></v-divider>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="12">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="date"
                    label="Date Paid"
                    prepend-inner-icon="mdi-calendar-month"
                    v-bind="attrs"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="date"
                  no-title
                  scrollable
                  @input="dateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="12">
              <v-textarea
                v-model="description"
                label="(Optional) Add a description"
                hide-details
                dense
                outlined
                class="my-1"
                rows="3"
              >
              </v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-file-input
                v-model="attachments"
                accept="image/*, application/pdf"
                label="Upload Attachments"
                prepend-icon=""
                chips
                small-chips
                multiple
                outlined
                dense
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-paperclip</v-icon>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pb-2" cols="12" sm="12" md="12">
              <v-checkbox v-model="confirmSendEmail" class="my-0" hide-details>
                <template v-slot:label> Send email to tenant </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="font-weight-bold"
            color="primary darken-1"
            text
            :disabled="!isFormValid"
            @click="finish"
            >Finish</v-btn
          >
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { DATEPICKER, dayjs } from '../../util/date'
import { formatCurrency, getCurrencyFieldValue } from '../../util/currency'

export default {
  data() {
    return {
      paidPayment: {},
      tenantId: -1,
      bedId: -1,
      bedName: '',
      dialog: false,
      options: {
        color: 'primary',
        width: 500,
        zIndex: 200
      },
      date: dayjs().format(DATEPICKER),
      datePicker: '',
      dateMenu: false,
      description: '',
      currentAttachments: [],
      attachments: [],
      loading: true,
      confirmSendEmail: false,
      isFormValid: false,
      formatCurrency,
      tenantName: '',
      unitName: '',
      amount: 0,
      amountPaid: 0,
      total: 0,
      useRemainingBalance: false,
      useBalanceHint: '',
      tenantBalance: 0,
      paymentRequests: [],
      securityDeposits: [],
      showSecurityDeposit: false,
      useSecurityDeposit: false,
      securityDepositAmountUsed: null,
      maxSecurityDepositAmount: null,
      securityDepositAmountRules: [
        (v) => {
          return (
            getCurrencyFieldValue(v) <= this.maxSecurityDepositAmount ||
            'Cannot exceed the usable security deposit amount'
          )
        }
        // (v) =>
        //   getCurrencyFieldValue(v) + this.amountPaid <= this.amount ||
        //   'Consumed security deposit cannot exceed the amount to pay'
      ]
    }
  },
  computed: {
    access() {
      return this.$root.$roleAccess('payment').access
    }
  },
  methods: {
    async open({ paymentRequests }) {
      this.currentAmountPaid = paymentRequests.reduce(
        (total, paymentRequest) =>
          total +
          (paymentRequest.amountPaid -
            (paymentRequest.adjustments || []).reduce(
              (a, b) => a + b.value,
              0
            ) || 0),
        0
      )

      if (paymentRequests && paymentRequests.length) {
        this.tenantName = paymentRequests[0].tenantName
        this.unitName = paymentRequests[0].unitName
      }
      this.dialog = true
      this.paymentRequests = paymentRequests
      this.loading = false
      this.total = paymentRequests.reduce(
        (total, paymentRequest) => total + paymentRequest.total,
        0
      )
      this.amount = this.total - this.currentAmountPaid
      this.amountPaid = this.total - this.currentAmountPaid
      this.leaseId =
        (paymentRequests.every(
          (item) => item.leaseId === paymentRequests[0].leaseId
        ) &&
          paymentRequests[0].leaseId) ||
        null
      this.useSecurityDeposit = false
      // this.tenantBalance = parseFloat(payment.tenantBalance)
      this.tenantBalance = await this.$store.dispatch(
        'units/fetchTenantBalanceAction',
        {
          tenantId: paymentRequests[0].tenantId
        }
      )
      if (this.tenantBalance > 0) {
        this.useRemainingBalance = true
        this.balanceUsed =
          this.tenantBalance - this.amount >= 0
            ? this.amount
            : this.tenantBalance
        this.useBalanceHint =
          this.tenantBalance > 0
            ? 'Amount reduced by ' + this.formatCurrency(this.balanceUsed)
            : 'Added debit of ' + this.formatCurrency(-this.balanceUsed)
        this.amount = this.total - this.balanceUsed - this.currentAmountPaid
        this.amountPaid = this.total - this.balanceUsed - this.currentAmountPaid
      } else {
        this.useRemainingBalance = false
        this.balanceUsed = 0
        this.useBalanceHint = ''
      }
      this.fetchSecurityDeposit()

      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async finish() {
      try {
        this.loading = true
        await this.$store.dispatch('payments/createCombinedPayments', {
          payments: this.paymentRequests,
          totalAmountPaid: this.amountPaid,
          description: this.description,
          datePaid: dayjs(this.date).format(),
          attachments: this.attachments.filter(
            (attachment) => attachment instanceof File
          ),
          leaseId: this.leaseId,
          tenantId: this.paymentRequests[0].tenantId,
          balanceUsed: this.balanceUsed,
          deductFromSecDep: this.useSecurityDeposit
            ? this.securityDepositAmountUsed
            : undefined
        })

        this.dialog = false
        this.loading = false

        this.$root
          .$confirm({
            title: 'Create combined payment',
            message: `The combined payment has been created`,
            reconfirmOnly: true,
            confirmText: 'OK',
            options: {
              color: 'success'
            }
          })
          .then(() => {
            this.resolve(true)
          })
      } catch (err) {
        this.loading = false
        this.$root.$confirm({
          title: 'Create combined payment',
          message: err,
          options: {
            color: 'error'
          },
          reconfirmOnly: true,
          confirmText: 'OK'
        })
      }
    },
    cancel() {
      this.dialog = false
      this.loading = false
    },
    truncate(input) {
      if (input.length > 7) {
        return input.substring(0, 7) + '...'
      }
      return input
    },
    useRemainingBalanceEvent() {
      // set the base value of amount and amound paid
      this.amount = this.total - this.currentAmountPaid
      this.amountPaid = this.amount
      let reduced = 0

      if (!this.useRemainingBalance && !this.useSecurityDeposit) {
        this.balanceUsed = 0
        this.useBalanceHint = ''
        this.amount = this.total - this.currentAmountPaid
        this.amountPaid = this.total - this.currentAmountPaid
        return
      }

      // this.useRemainingBalance = !this.useRemainingBalance
      if (this.useRemainingBalance) {
        this.balanceUsed = Math.min(this.tenantBalance, this.amount)
        reduced += this.balanceUsed

        this.amount = this.amount - this.balanceUsed
        this.amountPaid = this.amountPaid - this.balanceUsed
      }

      if (this.useSecurityDeposit) {
        this.securityDepositAmountUsed = Math.min(
          this.maxSecurityDepositAmount,
          this.amount
        )
        reduced += this.securityDepositAmountUsed
        this.amount -= this.securityDepositAmountUsed
        this.amountPaid -= this.securityDepositAmountUsed
      }

      this.useBalanceHint =
        reduced > 0
          ? 'Amount reduced by ' + this.formatCurrency(reduced)
          : 'Added debit of ' + this.formatCurrency(-reduced)
    },
    async fetchSecurityDeposit() {
      const { usableAmount, securityDeposits } = await this.$store.dispatch(
        'securityDeposits/fetchSecurityDepositAction',
        {
          leaseId: this.leaseId,
          tenantId: this.paymentRequests[0].tenantId
        }
      )

      if (!securityDeposits || !usableAmount) {
        return
      }

      this.securityDeposits = securityDeposits
      this.showSecurityDeposit = true
      this.maxSecurityDepositAmount = usableAmount
      this.securityDepositAmountUsed = Math.min(usableAmount, this.amount)
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}

.v-expansion-panel {
  border-style: solid;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.12);
}
</style>
